import { forwardRef, type HTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from './cn';

/**
 * Flat surface with a 1px line border (design-spec §2.5–2.7: "all other
 * surfaces are flat"). Pick the radius and padding the spec gives per card.
 *
 * - radius: 16 (radio/past rows), 18 (results, services, reviews, mobile),
 *   20 (home salon cards, booking aside, confirmation), 24 (big home panels)
 * - padding: none | sm 16 (result card) | md 20×22 (rows, reviews) |
 *   lg 26 (booking aside) | xl 44×52 (home steps panel) | mobile 14×16
 * - tone: surface (default) | sand | sand2 | dark
 * - elevated: the sticky "Book an appointment" shadow
 */
export type CardTone = 'surface' | 'sand' | 'sand2' | 'dark';
export type CardPadding = 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'mobile';
export type CardRadius = 16 | 18 | 20 | 24;

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  tone?: CardTone;
  padding?: CardPadding;
  radius?: CardRadius;
  elevated?: boolean;
  /** Render the child element (e.g. `<article>`, `<section>`, `<Link>`) instead of a div. */
  asChild?: boolean;
}

const tones: Record<CardTone, string> = {
  surface: 'bg-sl-surface border border-sl-line text-sl-ink',
  sand: 'bg-sl-sand text-sl-ink',
  sand2: 'bg-sl-sand-2 text-sl-ink',
  dark: 'bg-sl-dark text-sl-on-dark',
};

const paddings: Record<CardPadding, string> = {
  none: '',
  sm: 'p-4',
  md: 'px-[22px] py-5',
  lg: 'p-[26px]',
  xl: 'px-[52px] py-11',
  mobile: 'px-4 py-3.5',
};

const radii: Record<CardRadius, string> = {
  16: 'rounded-16',
  18: 'rounded-18',
  20: 'rounded-20',
  24: 'rounded-24',
};

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  {
    tone = 'surface',
    padding = 'md',
    radius = 18,
    elevated,
    asChild,
    className,
    ...rest
  },
  ref
) {
  const Comp = asChild ? Slot : 'div';
  return (
    <Comp
      ref={ref}
      className={cn(
        tones[tone],
        paddings[padding],
        radii[radius],
        elevated && 'shadow-book-card',
        className
      )}
      {...rest}
    />
  );
});
